import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";

import { LanguageProvider } from "@/context/LanguageContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Offers from "@/pages/Offers";
import Categories from "@/pages/Categories";
import About from "@/pages/About";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import LegalPage from "@/pages/Legal";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminContact from "@/pages/admin/AdminContact";
import AdminSettings from "@/pages/admin/AdminSettings";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
    return null;
}

function RequireAdmin({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-cream">
                <div className="h-10 w-10 rounded-full border-4 border-brand-green/20 border-t-brand-green animate-spin" />
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;
    return children;
}

function AppRoutes() {
    return (
        <BrowserRouter>
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/angebote" element={<Offers />} />
                <Route path="/kategorien" element={<Categories />} />
                <Route path="/ueber-uns" element={<About />} />
                <Route path="/karriere" element={<Careers />} />
                <Route path="/kontakt" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/impressum" element={<LegalPage kind="impressum" />} />
                <Route path="/datenschutz" element={<LegalPage kind="datenschutz" />} />
                <Route path="/cookies" element={<LegalPage kind="cookies" />} />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                    path="/admin"
                    element={<RequireAdmin><AdminLayout /></RequireAdmin>}
                >
                    <Route index element={<AdminDashboard />} />
                    <Route path="offers" element={<AdminOffers />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="faqs" element={<AdminFaqs />} />
                    <Route path="applications" element={<AdminApplications />} />
                    <Route path="newsletter" element={<AdminNewsletter />} />
                    <Route path="contact" element={<AdminContact />} />
                    <Route path="settings" element={<AdminSettings />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
}

export default function App() {
    return (
        <LanguageProvider>
            <SettingsProvider>
                <AuthProvider>
                    <AppRoutes />
                    <Toaster position="top-right" richColors closeButton />
                </AuthProvider>
            </SettingsProvider>
        </LanguageProvider>
    );
}
